import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useSWR from 'swr';
import axios from 'axios';
import { getTabNumber } from '@shared/commonFunctions';
import { Wrapper } from '@styles/CommonStyles';
import { IEmailTemplate, IEmailTemplates, ISMSTemplate, ISMSTemplates } from '@modules/admin/Tools/CustomerCommunications/types';
import { EC_SERVICE } from '@shared/constants/apiConfig';
import { VoicemailPreviewModal } from './components/VoicemailPreviewModal';
import { EditOneClickModal, IOneClickPayload } from './components/EditOneClickModal';
import { communicationTabsList, OneClickEditTabs } from './components/OneClickEditTabs/OneClickEditTabs';
import { CommunicationType, IOneClickCommunicationDataModel } from './interfaces';
import { requestCreateOneClickConfiguration, requestUpdateOneClickConfiguration } from './requests';
import { useOneClickCommunicationData } from './hooks/useOneClickCommunicationData';

export const OneClickEdit = () => {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [previewVoicemail, setVoicemail] = useState<IOneClickCommunicationDataModel | null>(null);
  const [previewVoicemailPosition, setPreviewVoicemailPosition] = useState(0);
  const [emailToEdit, setEmailToEdit] = useState<IOneClickCommunicationDataModel | null>(null);

  const [editEmailModalPosition, setEditEmailModalPosition] = useState(0);

  const location = useLocation();
  const communicationTypeId = useMemo(() => {
    let tabIndex = getTabNumber(location.search) - 1;
    tabIndex = tabIndex < 0 ? 0 : tabIndex;
    const tabName = communicationTabsList[tabIndex].name as keyof typeof CommunicationType;
    return CommunicationType[tabName];
  }, [location]);

  const fetchWithOneClick = async <T extends { isOneClickTemplate?: boolean }>(url: string) => {
    const { data } = await axios.get(url);
    return data?.filter((template: T) => template.isOneClickTemplate);
  };

  const { data: emailTemplates } = useSWR<IEmailTemplates>(
    `${EC_SERVICE.ENTERPRISECOMMUNICATION}/emailtemplate/all`,
    fetchWithOneClick<IEmailTemplate>
  );

  const { data: smsTemplates } = useSWR<ISMSTemplates>(
    `${EC_SERVICE.ENTERPRISECOMMUNICATION}/smstemplate`,
    fetchWithOneClick<ISMSTemplate>
  );


  const { refetchCommunicationData } = useOneClickCommunicationData(communicationTypeId, currentUser);

  const sendUpdatedCommunicationData = useCallback(
    async (oneClickPayload: IOneClickPayload) => {
      // eslint-disable-next-line no-extra-boolean-cast
      const newRecord = !Boolean(oneClickPayload.id);
      const requestData = { ...oneClickPayload, userId: newRecord ? currentUser : oneClickPayload.userId };

      if (newRecord) {
        await requestCreateOneClickConfiguration(requestData);
      } else {
        await requestUpdateOneClickConfiguration(requestData);
      }

      refetchCommunicationData();
    },
    [refetchCommunicationData, requestCreateOneClickConfiguration, requestUpdateOneClickConfiguration, currentUser]
  );

  const onSaveEmail = useCallback(
    (oneClickPayload: IOneClickPayload) => {
      if (!emailToEdit) return;

      sendUpdatedCommunicationData(oneClickPayload);
      setEmailToEdit(null);
      setEditEmailModalPosition(0);
    },
    [emailToEdit, sendUpdatedCommunicationData]
  );

  const handleEmailModalClose = useCallback(() => {
    setEmailToEdit(null);
    setEditEmailModalPosition(0);
  }, []);

  const handleeVoicemailPreviewModalClose = useCallback(() => {
    setVoicemail(null);
    setPreviewVoicemailPosition(0);
  }, []);

  return (
    <Wrapper>
      <VoicemailPreviewModal
        isOpen={!!previewVoicemail}
        voicemail={previewVoicemail}
        previewVoicemailPosition={previewVoicemailPosition}
        onClose={handleeVoicemailPreviewModalClose}
      />

      <EditOneClickModal
        communicationTypeId={communicationTypeId}
        isOpen={!!emailToEdit}
        oneClickData={emailToEdit}
        emailTemplates={emailTemplates ?? []}
        smsTemplates={smsTemplates ?? []}
        editEmailModalPosition={editEmailModalPosition}
        onSubmit={onSaveEmail}
        onClose={handleEmailModalClose}
      />

      <OneClickEditTabs
        setEmailToEdit={setEmailToEdit}
        setEditEmailModalPosition={setEditEmailModalPosition}
        setCurrentUser={setCurrentUser}
        setVoicemail={setVoicemail}
        setPreviewVoicemailPosition={setPreviewVoicemailPosition}
        currentUser={currentUser}
        emailTemplates={emailTemplates || null}
        smsTemplates={smsTemplates || null}
      />
    </Wrapper>
  );
};
